import { Form, Formik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useState } from 'react';
import Ripples from 'react-ripples';
import getTailwindConfig from '../../../helpers/get-tailwind-config';
import { LoqateAddressFindType } from '../../../models/loqate-address-find-type';
import { LoqateAddressRetrieveType } from '../../../models/loqate-address-retrieve-type';
import addressLookup from '../../../services/local-api/endpoints/address-lookup';
import addressRetrieve from '../../../services/local-api/endpoints/address-retrieve';
import FormText from '../../atoms/form-text/form-text';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';

interface Props {
  readonly countryCode: string;
  readonly ctaLabel: string;
  readonly label: string;
  readonly onEnterManually?: () => void;
  readonly onSelect: (address: LoqateAddressRetrieveType) => void;
  readonly placeholder: string;
}

const AddressLookup: FC<Props> = ({
  countryCode,
  ctaLabel,
  label,
  onEnterManually,
  onSelect,
  placeholder,
}: Props) => {
  const [addresses, setAddresses] = useState<LoqateAddressFindType[]>();
  const [debounce, setDebounce] = useState<NodeJS.Timeout>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [showEnterManually, setShowEnterManually] = useState<boolean>(true);
  const fullConfig = getTailwindConfig();
  const frenchBlue = fullConfig.theme.colors['french-blue'];

  const lookupAddress = async (text: string): Promise<void> => {
    setIsFetching(true);

    if (debounce) {
      clearTimeout(debounce);
    }

    if (!text) {
      setAddresses(null);
      setIsFetching(false);

      return;
    }

    setDebounce(
      setTimeout(async () => {
        const response = await addressLookup(countryCode, text);

        setAddresses(response);
        setIsFetching(false);
      }, 1000)
    );
  };

  const retrieveAddress = async (id: string): Promise<void> => {
    setIsFetching(true);

    const response = await addressRetrieve(id);

    setIsFetching(false);
    setAddresses(null);
    onSelect(response);
    setShowEnterManually(false);
  };

  return (
    <>
      <Formik
        initialValues={{
          searchTerm: '',
        }}
        onSubmit={({ searchTerm }) => lookupAddress(searchTerm)}
      >
        {({ setValues }) => (
          <>
            <Form>
              <FormText
                label={label}
                name="searchTerm"
                onChange={(value: string) => lookupAddress(value)}
                placeholder={placeholder}
                type="search"
              />
            </Form>

            {!addresses?.length && isFetching && <LoadingSpinner />}

            <AnimatePresence>
              {!!addresses?.length && (
                <motion.div
                  animate={{ height: 'auto', opacity: isFetching ? 0.4 : 1 }}
                  className={
                    `${isFetching ? 'pointer-events-none ' : ''}` +
                    'border border-grey-100 p-4 rounded-lg'
                  }
                  exit={{
                    height: 0,
                    opacity: 0,
                    transition: { duration: 0.2 },
                  }}
                  initial={{ height: 0, opacity: 0 }}
                >
                  <div className="custom-scrollbar-primary max-h-64 overflow-y-auto">
                    {addresses.map((address: LoqateAddressFindType) => (
                      <button
                        className="focus:bg-french-blue hover:bg-light-blue duration-150 my-2px focus:outline-none px-4 py-2 rounded-lg text-left focus:text-white transition-colors"
                        onClick={() => {
                          setValues({ searchTerm: '' });
                          retrieveAddress(address.Id);
                        }}
                        style={{ width: 'calc(100% - 1rem)' }}
                        type="button"
                      >
                        <span>
                          {address.Text} {address.Description}
                        </span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {showEnterManually && onEnterManually && (
              <div className="flex justify-center">
                <Ripples
                  className="rounded-full w-min"
                  color={`rgba(${frenchBlue.rgb}, .2)`}
                  during={1200}
                >
                  <button
                    className="flex items-center focus:outline-none px-4 py-2 rounded-full text-grey-600"
                    onClick={() => {
                      onEnterManually();
                      setShowEnterManually(false);
                    }}
                    type="button"
                  >
                    <IcomoonIcon color="french-blue" name="edit" size="lg" />

                    <span className="ml-4 whitespace-nowrap">{ctaLabel}</span>
                  </button>
                </Ripples>
              </div>
            )}
          </>
        )}
      </Formik>
    </>
  );
};

AddressLookup.defaultProps = {
  onEnterManually: null,
};

export default AddressLookup;
